/**
 * Presentational pieces of TanTable's totals bar (the toggleable aggregation
 * footer): the Σ toolbar icon, one footer slot per column, and the calc picker.
 *
 * All calculation lives in utils/tableAggregations.ts; TanTable owns the row
 * data and the persisted state. These components only draw.
 *
 * Design: docs/plans/2026-08-27-tantable-aggregation-footer-design.md
 */

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AGG_LABELS, type AggId } from '../utils/tableAggregations';

/**
 * Sigma mark for the toolbar toggle.
 *
 * The artwork fills its 24x24 box edge-to-edge, where heroicons leave roughly a
 * fifth of theirs as padding — so at the same element size the glyph reads too
 * large. The padded viewBox centers the same path at ~80% scale INSIDE a
 * full-size element, which keeps the button (and its selected background) the
 * same footprint as its neighbours. Do not shrink the element instead: that
 * shrinks the button with it.
 */
/* viewBox padded so the glyph fills ~72% of the box — the band the heroicons beside
   it in the TanTable toolbar occupy. Padding is on the viewBox, not the path. */
export const SigmaIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="-4.67 -4.67 33.33 33.33" fill="currentColor" aria-hidden="true">
    <path d="m18.5,24H2v-2.575l8.482-9.425L2,2.576V0h16.5c1.93,0,3.5,1.57,3.5,3.5v1.5h-3v-1.5c0-.276-.225-.5-.5-.5H6.418l8.1,9-8.1,9h12.082c.275,0,.5-.225.5-.5v-1.5h3v1.5c0,1.93-1.57,3.5-3.5,3.5Z" />
  </svg>
);

interface AggFooterCellProps {
  columnId: string;
  columnLabel: string;
  /** The chosen calc, or null for an empty slot. */
  calc: AggId | null;
  /** Formatted result for `calc`; ignored when there is no calc. */
  display: string | null;
  align: 'left' | 'center' | 'right';
  compact: boolean;
  /** Scope line ("Σ 128 rows"), rendered on the leading slot only. */
  hint?: string | null;
  /** Keep the "+" affordance visible instead of revealing it on hover. */
  alwaysShowAdd: boolean;
  onOpen: (columnId: string, anchor: HTMLElement) => void;
}

/** One slot in the totals row: shows a result, or invites the user to pick a calc. */
export const AggFooterCell = ({
  columnId,
  columnLabel,
  calc,
  display,
  align,
  compact,
  hint,
  alwaysShowAdd,
  onOpen,
}: AggFooterCellProps) => {
  const alignItems =
    align === 'right' ? 'items-end' : align === 'center' ? 'items-center' : 'items-start';

  return (
    <button
      type="button"
      data-testid={`agg-slot-${columnId}`}
      aria-label={
        calc
          ? `${columnLabel}: ${AGG_LABELS[calc]} — change calculation`
          : `Add calculation for ${columnLabel}`
      }
      onClick={(e) => onOpen(columnId, e.currentTarget)}
      className={`group flex w-full flex-col ${alignItems} ${
        compact ? 'gap-0' : 'gap-0.5'
      } rounded px-1 hover:bg-theme-bg-secondary transition-colors cursor-pointer`}
    >
      {hint && (
        <span
          data-testid="agg-scope-hint"
          className="text-[0.625rem] font-medium leading-tight text-muted whitespace-nowrap"
        >
          {hint}
        </span>
      )}
      {calc ? (
        <>
          <span className="leading-tight tabular-nums text-body">
            {display}
          </span>
          <span className="text-[0.625rem] font-normal uppercase tracking-wide leading-tight text-muted">
            {AGG_LABELS[calc]}
          </span>
        </>
      ) : (
        <span
          aria-hidden="true"
          className={`leading-tight text-gray-400 dark:text-gray-500 ${
            alwaysShowAdd ? '' : 'opacity-0 group-hover:opacity-100 transition-opacity'
          }`}
        >
          +
        </span>
      )}
    </button>
  );
};

interface AggCalcPickerProps {
  columnLabel: string;
  calcs: AggId[];
  current: AggId | null;
  /** Viewport position of the slot that opened the picker. */
  anchor: { x: number; y: number };
  onPick: (calc: AggId | null) => void;
  onClose: () => void;
  portalContainer?: HTMLElement | null;
}

/**
 * The calc menu. Opens upward from its slot — the totals row sits at the bottom
 * of the scroll container, so a downward menu would fall off the table.
 */
export const AggCalcPicker = ({
  columnLabel,
  calcs,
  current,
  anchor,
  onPick,
  onClose,
  portalContainer,
}: AggCalcPickerProps) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ x: anchor.x, y: anchor.y });

  // Flip above the slot, then clamp into the viewport.
  useEffect(() => {
    const rect = menuRef.current?.getBoundingClientRect();
    if (!rect) return;
    let y = anchor.y - rect.height - 6;
    if (y < 8) y = Math.min(anchor.y + 24, window.innerHeight - rect.height - 8);
    let x = anchor.x;
    if (x + rect.width > window.innerWidth) x = window.innerWidth - rect.width - 8;
    setPos({ x: Math.max(8, x), y: Math.max(8, y) });
  }, [anchor.x, anchor.y]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const onDown = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [onClose]);

  const itemClass =
    'flex items-center justify-between gap-3 w-full px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer whitespace-nowrap';

  return createPortal(
    <div
      ref={menuRef}
      data-testid="agg-calc-picker"
      className="fixed bg-panel border border-rule rounded-lg shadow-lg z-[9999] py-1 min-w-[180px]"
      style={{ left: pos.x, top: pos.y }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="px-3 py-1 text-[0.625rem] uppercase tracking-wide text-muted border-b border-rule mb-1 truncate max-w-[240px]">
        {columnLabel}
      </div>
      {calcs.length === 0 && (
        <div className="px-3 py-1.5 text-sm text-gray-400 dark:text-gray-500">
          No calculations available
        </div>
      )}
      {calcs.map((id) => (
        <button key={id} type="button" className={itemClass} onClick={() => onPick(id)}>
          <span>{AGG_LABELS[id]}</span>
          {current === id && <span aria-hidden="true">✓</span>}
        </button>
      ))}
      {current && (
        <>
          <div className="border-t border-rule my-1" />
          <button type="button" className={itemClass} onClick={() => onPick(null)}>
            <span>None</span>
          </button>
        </>
      )}
    </div>,
    portalContainer || document.body,
  );
};
